/**
 * Route resolver for the single-segment `/events/:segment` shape.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE COLLISION
 *
 * The brief asks for `/events/tonight`, `/events/this-weekend` and
 * `/events/:town`. `/events/:id` already exists and is the SEO surface for
 * all 22 approved events — it is emitted into the sitemap
 * (api-server/src/routes/seo.ts), carries server-side OG tags
 * (social-og.ts:195) and Schema.org JSON-LD, is a universal-link target
 * (app.json), and is the deep link in every "your event was approved" push
 * payload already delivered (`{screen: "/events/<id>"}`, events.ts:607).
 *
 * All four shapes are one path segment. Something has to disambiguate them,
 * and it cannot be a redirect: audit R10 is explicit that changing these URLs
 * breaks binaries already installed on phones.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE RULE
 *
 *   1. a known date-preset slug   → What's On   (tonight, this-weekend, …)
 *   2. a known Ayrshire town slug → What's On   (kilmarnock, ayr, troon, …)
 *   3. anything else              → Event detail (numeric id or event slug)
 *
 * Deterministic, no request, no flash of the wrong page.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * IS IT SAFE? — checked against the live data, not assumed
 *
 * The 22 indexed slugs are long titles: `kilmarnock-fair-festival-we-make-
 * kilmarnock`, `faz-ahern-live-at-the-howard-arms`, `karaoke`, `live-music`,
 * `sunday-funday`, `karaoke-30`… None equals a town name or a preset slug.
 * Note `kilmarnocks-strongest-novice` — close, but not `kilmarnock`.
 *
 * The residual risk is a FUTURE event whose slug happens to be a town name,
 * e.g. an event called literally "Ayr". The town would win and the event
 * would become unreachable at `/events/ayr`. Two things contain that:
 *   • it stays reachable at `/event/<id>` and `/events/<id>`, both of which
 *     resolve, and the sitemap can emit the id form; and
 *   • slug generation is server-side, so Phase 4 can cheaply refuse to mint a
 *     slug that collides with a town or preset. That is the proper fix and it
 *     is recorded in the phase report.
 */
import { useParams } from "wouter";
import { DATE_PRESET_BY_SLUG, townFromSlug } from "@/components/hub";
import { useTown } from "@/hooks/use-town";
import WhatsOn from "./WhatsOn";
import EventDetail from "./EventDetail";

export default function EventsRoute() {
  const { segment } = useParams<{ segment: string }>();
  const { towns } = useTown();

  const raw = (segment ?? "").toLowerCase();

  if (DATE_PRESET_BY_SLUG[raw]) return <WhatsOn />;
  // `towns` includes the "All Towns" sentinel; townFromSlug only matches real
  // town names, so "all-towns" falls through to the detail page as it should.
  if (townFromSlug(raw, towns)) return <WhatsOn />;

  return <EventDetail />;
}
